import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {AccountLogo} from '../../agency/accounts_v2/AccountParts';
import {platformName} from '../../../utils/filterNotes';
import {ownAmount} from './UncontrolledItem';
import {joinNames} from './uncontrolledText';

const RAW = {interpolation: {escapeValue: false}};
const NO_CLIENT_PATH = '/agency/ad-accounts?vista=sin-cliente';

function rowKey(row) {
    return `${row.channel}|${row.account_id}`;
}

/** Un descarte es la cuenta entera o una campaña: dos campañas de la misma cuenta son filas distintas. */
function dismissedKey(row) {
    return `${row.channel}|${row.account_id}|${row.campaign_id || ''}`;
}

/**
 * Las filas con un pedido en vuelo. Mientras dura, los botones de ESA fila no se aprietan (un doble clic
 * en «Crear una línea» mandaba dos altas y podía dejar dos líneas para la misma cuenta); las demás filas
 * siguen activas. Se libera al volver, salga bien o mal.
 */
function useBusyRows(keyOf = rowKey) {
    const [busy, setBusy] = useState({});
    const run = (row, promise) => {
        const key = keyOf(row);
        setBusy((current) => ({...current, [key]: true}));
        return promise.finally(() => setBusy((current) => ({...current, [key]: false})));
    };
    return [(row) => !!busy[keyOf(row)], run];
}

/**
 * «N cuentas sin línea ni límite no registran inversión esta semana». Cada fila dice si Dashbo la mide:
 * una cuenta sin línea que el sync no trae puede estar gastando, y no se pinta como un cero.
 */
export function IdleBlock({rows, count, unmeasuredCount, resolved, actions}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [isBusy, run] = useBusyRows();
    if (!count) return null;

    return (
        <section className="alc-card alc-unc-block" data-testid="alc-unc-idle">
            <div className="alc-unc-block__head">
                <div>
                    <h2 className="alc-card__title">{t('alerts_center.uncontrolled.idle.title', {count})}</h2>
                    <p className="alc-unc-block__lede">
                        {t('alerts_center.uncontrolled.idle.lede')}
                        {unmeasuredCount > 0 && ` ${t('alerts_center.uncontrolled.idle.unmeasured', {count: unmeasuredCount})}`}
                    </p>
                </div>
                <button type="button" className="alc-btn -ghost" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
                    {open ? t('alerts_center.uncontrolled.close') : t('alerts_center.uncontrolled.idle.open', {count})}
                </button>
            </div>
            {open && (
                <ul className="alc-unc-rows">
                    {rows.map((row) => {
                        const done = resolved[rowKey(row)];
                        const busy = isBusy(row);
                        return (
                            <li key={rowKey(row)} className="alc-unc-row" aria-busy={busy || undefined}>
                                <span className="alc-unc-row__who">
                                    <AccountLogo channel={row.channel} size={22}/>
                                    <span>
                                        <span className="alc-unc-row__name">{row.account_name || row.account_id}</span>
                                        <span className="alc-unc-row__meta">
                                            {t('alerts_center.uncontrolled.idle.meta', {channel: platformName(row.channel), client: row.client_name, ...RAW})}
                                        </span>
                                    </span>
                                </span>
                                <span className={`alc-unc-tag ${row.measured ? '-quiet' : '-unmeasured'}`}>
                                    {row.measured ? t('alerts_center.uncontrolled.idle.measured') : t('alerts_center.uncontrolled.idle.not_measured')}
                                </span>
                                {done ? (
                                    <span className="alc-unc-row__done" role="status">
                                        {t(`alerts_center.uncontrolled.idle.done_${done}`)}
                                        {done === 'dismissed' && (
                                            <button type="button" className="alc-link -button" disabled={busy}
                                                    onClick={() => run(row, actions.undoIdle(row))}>
                                                {t('alerts_center.uncontrolled.undo')}
                                            </button>
                                        )}
                                    </span>
                                ) : (
                                    <span className="alc-unc-actions -row">
                                        <button type="button" className="alc-btn -ghost" disabled={busy}
                                                onClick={() => run(row, actions.createLineForRow(row))}>
                                            {t('alerts_center.uncontrolled.item.create_line')}
                                        </button>
                                        <Link className="alc-btn -ghost" to={`/client/${row.client_id}/connections`}>
                                            {t('alerts_center.uncontrolled.idle.set_limit')}
                                        </Link>
                                        <button type="button" className="alc-btn -ghost" disabled={busy}
                                                onClick={() => run(row, actions.dismissIdle(row))}>
                                            {t('alerts_center.uncontrolled.idle.not_used')}
                                        </button>
                                    </span>
                                )}
                            </li>
                        );
                    })}
                    {count > rows.length && (
                        <li className="alc-unc-rows__more">{t('alerts_center.uncontrolled.more_accounts', {count: count - rows.length})}</li>
                    )}
                </ul>
            )}
        </section>
    );
}

/**
 * «N cuentas que ven tus conexiones no son de ningún cliente» (UD-5, 17/10): sólo el conteo, unos nombres de
 * ejemplo y el link a Conexiones, que es donde se asignan a un cliente o se marcan «No es de esta agencia».
 * SIN montos: el gasto de una cuenta sin cliente sólo se conoce si otra agencia la sincroniza, y no es plata
 * de esta agencia. No suma al total de arriba.
 */
export function NoClientBlock({block}) {
    const {t} = useTranslation();
    const count = (block && Number(block.count)) || 0;
    if (!count) return null;
    const sample = (block && Array.isArray(block.sample) ? block.sample : []).filter(Boolean);

    return (
        <section className="alc-card alc-unc-block" data-testid="alc-unc-no-client">
            <h2 className="alc-card__title">{t('alerts_center.uncontrolled.no_client.title', {count})}</h2>
            <p className="alc-unc-block__lede">{t('alerts_center.uncontrolled.no_client.lede')}</p>
            {sample.length > 0 && (
                <p className="alc-unc-block__lede -sample">
                    {t('alerts_center.uncontrolled.no_client.sample', {names: joinNames(sample, t), ...RAW})}
                </p>
            )}
            <p className="alc-unc-block__foot">
                <Link className="alc-link" to={NO_CLIENT_PATH}>{t('alerts_center.uncontrolled.no_client.see_in_connections')}</Link>
            </p>
        </section>
    );
}

/**
 * «Lo que dejaste de mostrar»: las cuentas y campañas con «No hace falta controlarla», con su motivo y
 * «Volver a mostrar». El «Deshacer» de la tarjeta dura lo que la visita; sin este bloque, un descarte
 * hecho sin querer no tenía vuelta desde la pantalla (QA del 11/10). Al volver a mostrar, la pantalla
 * recarga el informe y la cuenta vuelve a su tarjeta con el monto de hoy.
 */
export function DismissedBlock({rows, count, agencyCurrency, actions}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [isBusy, run] = useBusyRows(dismissedKey);
    if (!count) return null;

    return (
        <section className="alc-card alc-unc-block" data-testid="alc-unc-dismissed">
            <div className="alc-unc-block__head">
                <div>
                    <h2 className="alc-card__title">{t('alerts_center.uncontrolled.dismissed.title', {count})}</h2>
                    <p className="alc-unc-block__lede">{t('alerts_center.uncontrolled.dismissed.lede')}</p>
                </div>
                <button type="button" className="alc-btn -ghost" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
                    {open ? t('alerts_center.uncontrolled.close') : t('alerts_center.uncontrolled.dismissed.open', {count})}
                </button>
            </div>
            {open && (
                <ul className="alc-unc-rows">
                    {rows.map((row) => {
                        const busy = isBusy(row);
                        const isCampaign = !!row.campaign_id;
                        return (
                            <li key={dismissedKey(row)} className="alc-unc-row" aria-busy={busy || undefined}>
                                <span className="alc-unc-row__who">
                                    <AccountLogo channel={row.channel} size={22}/>
                                    <span>
                                        <span className="alc-unc-row__name">
                                            {isCampaign ? (row.campaign_name || row.campaign_id) : (row.account_name || row.account_id)}
                                        </span>
                                        <span className="alc-unc-row__meta">
                                            {isCampaign
                                                ? t('alerts_center.uncontrolled.dismissed.campaign_meta', {
                                                    account: row.account_name || row.account_id, client: row.client_name, ...RAW})
                                                : t('alerts_center.uncontrolled.idle.meta', {channel: platformName(row.channel), client: row.client_name, ...RAW})}
                                            {' · '}
                                            {row.amount > 0
                                                ? t('alerts_center.uncontrolled.dismissed.amount', {
                                                    amount: ownAmount(row.amount, row.currency, agencyCurrency), ...RAW})
                                                : t('alerts_center.uncontrolled.dismissed.no_spend')}
                                        </span>
                                    </span>
                                </span>
                                <span className="alc-unc-tag -quiet">
                                    {row.reason
                                        ? t(`alerts_center.uncontrolled.dismiss.reason.${row.reason}`)
                                        : t('alerts_center.uncontrolled.dismissed.no_reason')}
                                </span>
                                <span className="alc-unc-actions -row">
                                    <button type="button" className="alc-btn -ghost" disabled={busy}
                                            onClick={() => run(row, actions.restore(row))}>
                                        {t('alerts_center.uncontrolled.dismissed.restore')}
                                    </button>
                                </span>
                            </li>
                        );
                    })}
                    {count > rows.length && (
                        <li className="alc-unc-rows__more">{t('alerts_center.uncontrolled.dismissed.more', {count: count - rows.length})}</li>
                    )}
                </ul>
            )}
        </section>
    );
}
